import { Injectable } from "@angular/core";
import { ICollection } from "@gtmhub/models";
import { INavItem, NavItemsType } from "@webapp/navigation/models/nav-items-list.models";

import { BehaviorSubject, concatMap, map, Observable, Subject, tap } from "rxjs";
import { IFavoritesModel, FavoritesDtoModel } from "./favorites.models";
import { BaseFacade } from "@webapp/core/abstracts/services/base-facade.service";
import { FavoritesState } from "./favorites.state";
import { FavoritesApiService } from "./favorites.api.service";
import { IUXCustomizationItemResponse, UXCustomizationType } from "@gtmhub/uxcustomization/models";
import { FavoritesFactory } from "@gtmhub/favorites/favorites-factory";
import { FavoritesPayloadBuilder } from "@webapp/navigation/utils/favorites-payload.builder";
import { RequestPaging } from "@webapp/core/abstracts/models/request.paging";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { buildNavItemFromModel, favoritesGroupedReponseToNavItems, getNavItemsType, SubNavItemsModel } from "@webapp/navigation/utils/nav-items.util";
import { IFavoritesItemsGroupedResponse } from "@gtmhub/favorites/models";

@Injectable({
  providedIn: "root",
})
export class FavoritesFacade extends BaseFacade<IFavoritesModel, FavoritesDtoModel, FavoritesState, FavoritesApiService> {
  private reorderRequests$ = new Subject<() => Observable<unknown>>();
  private favorites$: BehaviorSubject<{ [key in NavItemsType]?: ICollection<INavItem> }> = new BehaviorSubject({});

  public markItemAsFavorite(
    type: UXCustomizationType,
    id: string,
    navItem: INavItem,
    navItemsType: NavItemsType
  ): Observable<IUXCustomizationItemResponse<UXCustomizationType>> {
    const payload = this.favoritesPayloadBuilder.forType(type).withItem({ id }).generate();
    return this.api.post$<IUXCustomizationItemResponse<UXCustomizationType>>(payload).pipe(
      tap((response: IUXCustomizationItemResponse<UXCustomizationType>) => {
        const targetedCollection = this.favorites$.value[navItemsType];
        if (!targetedCollection) return;

        this.favorites$.next({
          ...this.favorites$.value,
          [navItemsType]: {
            ...targetedCollection,
            items: [{ ...navItem, id: response.itemId, favoriteId: response.id }, ...targetedCollection.items],
            totalCount: targetedCollection.totalCount + 1,
          },
        });
      })
    );
  }

  public unmarkItemAsFavorite(id: string, navItem: INavItem, navItemsType: NavItemsType): Observable<IFavoritesModel> {
    return this.api.delete$(id).pipe(
      tap(() => {
        const targetedCollection = this.favorites$.value[navItemsType];
        if (!targetedCollection) return;

        const collectionItem = targetedCollection.items.find((item) => item.favoriteId === navItem.favoriteId);
        if (collectionItem) {
          this.favorites$.next({
            ...this.favorites$.value,
            [navItemsType]: {
              ...targetedCollection,
              items: targetedCollection.items.filter((item) => item.favoriteId !== navItem.favoriteId),
              totalCount: targetedCollection.totalCount - 1,
            },
          });
        }
      })
    );
  }

  public constructor(
    state: FavoritesState,
    private api: FavoritesApiService,
    private favoritesFactory: FavoritesFactory,
    private favoritesPayloadBuilder: FavoritesPayloadBuilder,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    super(state, api);
    this.reorderRequests$
      .pipe(
        concatMap((r) => {
          return r();
        })
      )
      .subscribe({
        error: (err) => {
          this.uiErrorHandlingService.handleModal(err);
        },
      });
  }

  public getFavoritesOfType(entityNavType: NavItemsType): Observable<ICollection<INavItem>> {
    return this.favorites$.asObservable().pipe(map((value) => value[entityNavType]));
  }

  public getAllOfType(entityTypes: UXCustomizationType[], navItemsType: NavItemsType): void {
    if (this.favorites$.value[navItemsType]) return;
    const favoriteItemsRequestParams: RequestPaging = this.favoritesFactory.buildNavigationFavoritesRequestPaging(entityTypes);
    this.api.getAll$<IFavoritesItemsGroupedResponse>(favoriteItemsRequestParams).subscribe((response: IFavoritesItemsGroupedResponse) => {
      this.favorites$.next({
        ...this.favorites$.value,
        [navItemsType]: favoritesGroupedReponseToNavItems(response, navItemsType),
      });
    });
  }

  public reorderFavoriteItemSequentially(favoriteId: string, newOrder: number, uxcType: UXCustomizationType | null = null): void {
    const navItemType = getNavItemsType(uxcType);
    if (navItemType && this.favorites$.value[navItemType].totalCount <= 1) return;
    this.reorderRequests$.next(() => this.api.reorderFavoriteItem(favoriteId, newOrder));
  }

  public updateItem(uxcType: UXCustomizationType, item: SubNavItemsModel): void {
    const navItemType = getNavItemsType(uxcType);
    if (!this.favorites$.value[navItemType]) return;

    const newNavItem = buildNavItemFromModel(uxcType, item);
    if (newNavItem !== null) {
      this.favorites$.next({
        ...this.favorites$.value,
        [navItemType]: {
          ...this.favorites$.value[navItemType],
          items: this.favorites$.value[navItemType].items.map((oldValue) => {
            return oldValue.id === item.id ? { ...oldValue, ...newNavItem } : oldValue;
          }),
        },
      });
    }
  }

  public deleteItem(uxcType: UXCustomizationType, item: SubNavItemsModel): void {
    const navItemType = getNavItemsType(uxcType);
    if (!this.favorites$.value[navItemType]) return;

    const newNavItem = buildNavItemFromModel(uxcType, item);
    const itemToBeDeleted = this.favorites$.value[navItemType].items.find((favoriteItem) => favoriteItem.id === item.id);
    if (newNavItem !== null && itemToBeDeleted) {
      this.favorites$.next({
        ...this.favorites$.value,
        [navItemType]: {
          ...this.favorites$.value[navItemType],
          items: this.favorites$.value[navItemType].items.filter((oldItem) => oldItem.id !== item.id),
          totalCount: this.favorites$.value[navItemType].totalCount - 1,
        },
      });
    }
  }
}
